import { createContext, useReducer } from 'react';
import type { ReactElement, ReactNode } from 'react';

import MenuItem from 'models/MenuItem';
import type { IMenuItem } from 'models/MenuItem';
import FetchState from 'models/FetchState';

type Props = { children: ReactNode; };
type Dispatcher = (action: Action) => void;

type Action =
  | { type: 'reload'; data: IMenuItem[]; }
  | { type: 'setFetchState'; data: FetchState; };

type ContextData = {
  favorites: MenuItem[];
  fetchState: FetchState;
};

type ContextValue = ContextData & { dispatch: Dispatcher };

const FavoritesContext = createContext<ContextValue | null>(null);

function favoritesReducer(value: ContextValue, action: Action): ContextData {
  const { favorites, fetchState } = value;

  switch (action.type) {
    case 'reload': {
      return {
        fetchState,
        favorites: action.data.map((favorite) => new MenuItem(favorite)),
      };
    }

    case 'setFetchState': {
      return { favorites, fetchState: action.data };
    }

    default: {
      return value;
    }
  }
}

function FavoritesProvider({ children }: Props): ReactElement {
  const [value, dispatch] = useReducer(favoritesReducer, {
    fetchState: FetchState.IN_PROGRESS,
    favorites: [],
  });

  const { favorites, fetchState } = value;

  return (
    <FavoritesContext.Provider value={{ favorites, dispatch, fetchState }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export type { ContextValue };
export { FavoritesContext, FavoritesProvider };
